/**
 * Web3Forms Submission Utility
 * 
 * Reusable utility for submitting lead generation forms to Web3Forms API
 * with response validation, error handling, metadata enrichment, and UTM/GCLID attribution.
 */

const WEB3FORMS_API_URL = 'https://api.web3forms.com/submit';

// Service mappings for human-readable lead notification emails
export const SERVICE_MAPPING = {
  'corporate-tax-filing': 'Corporate Tax (Filing only)',
  'corporate-tax-filing-payable': 'Corporate Tax (Filing + Tax Payable)',
  'Corporate Tax (Filing only)': 'Corporate Tax (Filing only)',
  'Corporate Tax (Filing + Tax Payable)': 'Corporate Tax (Filing + Tax Payable)'
};

/**
 * Extract marketing & attribution parameters from URL query string
 * @returns {Object} Extracted attribution key-value pairs
 */
export const getAttributionData = () => {
  if (typeof window === 'undefined' || !window.location) {
    return {};
  }

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const attribution = {};

    const trackableKeys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'gclid',
      'fbclid',
      'msclkid',
      'ttclid'
    ];

    trackableKeys.forEach((key) => {
      const value = urlParams.get(key);
      if (value) {
        attribution[key] = value;
      }
    });

    return attribution;
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Web3Forms] Error parsing attribution parameters:', err);
    }
    return {};
  }
};

/**
 * Submit form data to Web3Forms API
 * 
 * @param {Object} formData - Form input values (name, phone, email, company, service, etc.)
 * @param {Object} [options] - Additional submission options and metadata
 * @param {string} [options.form_name] - Identifier for the form (e.g. 'corporate-tax-hero')
 * @param {string} [options.subject] - Email subject line
 * @param {string} [options.access_key] - Override access key (defaults to REACT_APP_WEB3FORMS_ACCESS_KEY)
 * @param {string} [options.from_name] - Sender name displayed in notification email
 * @returns {Promise<{success: boolean, message: string, data?: Object}>} Standardized submission response
 */
export const submitToWeb3Forms = async (formData, options = {}) => {
  const accessKey = options.access_key || process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

  if (!accessKey) {
    const missingKeyMsg = 'Web3Forms Access Key is not configured. Please set REACT_APP_WEB3FORMS_ACCESS_KEY.';
    if (process.env.NODE_ENV === 'development') {
      console.error(`[Web3Forms] ${missingKeyMsg}`);
    }
    return {
      success: false,
      message: 'Submission service is temporarily unconfigured. Please contact us directly on WhatsApp.'
    };
  }

  const formName = options.form_name || 'lead-form';
  const defaultSubject = options.subject || 'New Website Lead - Standard Auditing';

  // Map service value if predefined mapping exists
  const resolvedService = formData.service
    ? (SERVICE_MAPPING[formData.service] || formData.service)
    : undefined;

  // Gather current page URL & attribution
  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  const landingPage = typeof window !== 'undefined' ? window.location.pathname : '';
  const referrer = typeof document !== 'undefined' && document.referrer ? document.referrer : '';
  const attribution = getAttributionData();

  // Construct structured payload for Web3Forms JSON API
  const payload = {
    access_key: accessKey,
    subject: defaultSubject,
    from_name: options.from_name || 'Standard Auditing Landing Page',
    form_name: formName,
    landing_page: landingPage,
    page_url: pageUrl,
    referrer: referrer || 'Direct / None',
    name: formData.name ? formData.name.trim() : '',
    phone: formData.phone ? formData.phone.trim() : '',
    email: formData.email ? formData.email.trim() : '',
    company: formData.company ? formData.company.trim() : 'Not provided',
    ...(resolvedService ? { service: resolvedService } : {}),
    ...(formData.details ? { details: formData.details.trim() } : {}),
    ...attribution
  };

  if (process.env.NODE_ENV === 'development') {
    console.log(`[Web3Forms] Submitting form: "${formName}" on page "${landingPage}"`);
  }

  try {
    const response = await fetch(WEB3FORMS_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[Web3Forms] Failed to parse JSON response:', parseError);
      }
      return {
        success: false,
        message: "We couldn't submit your request right now. Please try again or contact us directly on WhatsApp."
      };
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`[Web3Forms] Response status: ${response.status}`, data);
    }

    if (!response.ok || !data.success) {
      const errorMessage = data && data.message
        ? data.message
        : "We couldn't submit your request right now. Please try again or contact us directly on WhatsApp.";

      return {
        success: false,
        message: errorMessage
      };
    }

    return {
      success: true,
      message: data.message || 'Thank you! Your request has been received.',
      data: data
    };

  } catch (networkError) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Web3Forms] Network/fetch error:', networkError);
    }

    return {
      success: false,
      message: "Network error. Please check your connection and try again, or contact us directly on WhatsApp."
    };
  }
};
